import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, useTheme, useTenant } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import AdRotator from '../../components/AdRotator';
import { Sun, Moon, ArrowLeft, User, Ticket, MessageSquare, Settings, Save, LogOut, Star } from 'lucide-react';
import SenArtisteLogo from '../../components/SenArtisteLogo';
import { jsPDF } from 'jspdf';

const FanAccount = () => {
  const { artistSlug } = useParams();
  const navigate = useNavigate();
  const { user, logout, api, updateProfile } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { tenant, fetchTenant } = useTenant();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
  });
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'FAN') {
      navigate(`/${artistSlug}/login`);
      return;
    }
    
    setProfileForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phone: user.phone || '',
    });
    
    fetchTenant(artistSlug);
    fetchMyTickets();
  }, [user, artistSlug]);

  const fetchMyTickets = async () => {
    try {
      const response = await api.get('/tickets/my-tickets');
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(profileForm);
      toast.success('Profil mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const downloadTicketPDF = (ticket) => {
    const doc = new jsPDF();
    const artist = tenant?.artist;
    
    // Header
    doc.setFillColor(212, 175, 55);
    doc.rect(0, 0, 210, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('SEN ARTISTE', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.text('BILLET D\'ENTRÉE', 105, 32, { align: 'center' });
    
    // Event info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(ticket.event?.title || 'Événement', 105, 60, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text(`Artiste: ${artist?.nomDeScene || artistSlug}`, 20, 80);
    if (ticket.event?.dateTime) {
      doc.text(`Date: ${formatDate(ticket.event.dateTime)}`, 20, 90);
    }
    if (ticket.event?.location) {
      doc.text(`Lieu: ${ticket.event.location}`, 20, 100);
    }
    
    // Ticket number
    doc.setFillColor(240, 240, 240);
    doc.rect(20, 115, 170, 25, 'F');
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`N° ${ticket.ticketNumber}`, 105, 130, { align: 'center' });
    
    // Buyer info
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Acheteur: ${ticket.buyerName}`, 20, 155);
    doc.text(`Email: ${ticket.buyerEmail}`, 20, 165);
    
    // QR Code
    if (ticket.qrCodeData) {
      const qrImage = `data:image/png;base64,${ticket.qrCodeData}`;
      doc.addImage(qrImage, 'PNG', 70, 180, 70, 70);
    }
    
    // Footer
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text('Ce billet est personnel et non transférable.', 105, 265, { align: 'center' });
    doc.text('Présentez ce QR code à l\'entrée.', 105, 272, { align: 'center' });
    
    doc.save(`billet-${ticket.ticketNumber}.pdf`);
  };

  const handleLogout = () => {
    logout();
    navigate(`/${artistSlug}`);
  };

  const artist = tenant?.artist;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(`/${artistSlug}`)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Fanpage
          </Button>
          
          <div className="flex items-center gap-2">
            <SenArtisteLogo className="w-6 h-6" />
            <span className="font-syne font-bold">Mon Compte</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="max-w-4xl mx-auto px-4 flex gap-1">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'profile' ? 'border-primary text-primary' : 'border-transparent hover:text-primary'}`}
            data-testid="tab-profile"
          >
            <User className="w-4 h-4 inline mr-2" />
            Profil
          </button>
          <button
            onClick={() => setActiveTab('tickets')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'tickets' ? 'border-primary text-primary' : 'border-transparent hover:text-primary'}`}
            data-testid="tab-tickets"
          >
            <Ticket className="w-4 h-4 inline mr-2" />
            Mes Billets
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 md:p-8 space-y-6">
        {/* Ad Banner */}
        <AdRotator tenantSlug={artistSlug} />

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Mon Profil
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Prénom</Label>
                    <Input 
                      value={profileForm.firstName} 
                      onChange={(e) => setProfileForm(p => ({ ...p, firstName: e.target.value }))}
                      data-testid="fan-firstName"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nom</Label>
                    <Input 
                      value={profileForm.lastName} 
                      onChange={(e) => setProfileForm(p => ({ ...p, lastName: e.target.value }))}
                      data-testid="fan-lastName"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input 
                    value={profileForm.phone} 
                    onChange={(e) => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                    data-testid="fan-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={user?.email || ''} disabled className="bg-muted" />
                </div>
                <Button onClick={handleSaveProfile} disabled={saving} className="btn-primary" data-testid="save-fan-profile">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Tickets Tab */}
        {activeTab === 'tickets' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <h2 className="font-syne text-2xl font-bold">Mes Billets</h2>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
              </div>
            ) : tickets.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="p-8 text-center">
                  <Ticket className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">Vous n'avez pas encore de billets</p>
                  <Button onClick={() => navigate(`/${artistSlug}`)} style={{ backgroundColor: artist?.primaryColor }}>
                    Voir les événements
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {tickets.map((ticket) => (
                  <Card key={ticket.id} className="glass-card" data-testid={`fan-ticket-${ticket.id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <p className="font-mono text-lg font-bold">{ticket.ticketNumber}</p>
                          {ticket.event && (
                            <>
                              <p className="font-medium">{ticket.event.title}</p>
                              <p className="text-sm text-muted-foreground">
                                {formatDate(ticket.event.dateTime)} • {ticket.event.location}
                              </p>
                            </>
                          )}
                          <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${
                            ticket.status === 'VALID' ? 'badge-valid' :
                            ticket.status === 'USED' ? 'badge-used' : 'badge-cancelled'
                          }`}>
                            {ticket.status === 'VALID' ? 'Valide' : ticket.status === 'USED' ? 'Utilisé' : 'Annulé'}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {ticket.qrCodeData && (
                            <img 
                              src={`data:image/png;base64,${ticket.qrCodeData}`} 
                              alt="QR Code" 
                              className="w-20 h-20"
                            />
                          )}
                          <Button 
                            variant="outline"
                            onClick={() => downloadTicketPDF(ticket)}
                            data-testid={`download-fan-ticket-${ticket.id}`}
                          >
                            Télécharger PDF
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default FanAccount;
